import React from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { afTrack } from "../analytics/appsflyer";

function num(n, fallback = 0) {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

function fmtUsd(n) {
  return num(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function fmtSats(n) {
  return Math.round(num(n)).toLocaleString();
}

function fmtBtc(n) {
  const v = num(n);
  if (v <= 0) return "0.00000000";
  return v.toFixed(8);
}

function fmtPrice(n) {
  const v = num(n);
  if (v <= 0) return "—";
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${v.toFixed(2)}%`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function statusPill(status) {
  const s = String(status || "").toLowerCase();

  if (s === "active" || s === "filled" || s === "partial") {
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  }

  if (s === "paused") {
    return "border-yellow-500/30 bg-yellow-500/10 text-yellow-200";
  }

  if (s === "failed" || s === "cancelled") {
    return "border-red-500/30 bg-red-500/10 text-red-200";
  }

  return "border-zinc-700 bg-zinc-900 text-zinc-300";
}

function getTradeSats(t) {
  if (t?.sats != null) return num(t.sats);

  const btc =
    t?.btc_amount ??
    t?.btc_amt ??
    t?.btc;

  return num(btc) * 100_000_000;
}

/**
 * Entry context only: the backend returns percentage points, not a fraction.
 * This is NOT realized performance vs scheduled DCA.
 */
function getEntryPct(trade) {
  if (trade?.entry_benchmark !== "window_start" || trade?.entry_pct == null) {
    return null;
  }
  if (!['filled', 'partial'].includes(String(trade.status || '').toLowerCase())) {
    return null;
  }
  const value = Number(trade.entry_pct);
  // Only display a below-start entry when it actually is below start.
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Final partner performance belongs to completed windows, not trades.
 * Guard on benchmark so an old backend cannot be mislabeled during rollout.
 */
function getCompletedResult(totals, benchmark) {
  if (benchmark !== "scheduled_window_end" || !totals) return null;

  const completedWindows = Number(totals.completed_windows);
  if (!Number.isInteger(completedWindows) || completedWindows <= 0) return null;
  if (totals.extra_sats == null || totals.extra_pct == null) return null;

  const deltaSats = Number(totals.extra_sats);
  const gainPct = Number(totals.extra_pct);
  if (!Number.isFinite(deltaSats) || !Number.isFinite(gainPct)) return null;

  return { deltaSats, gainPct, completedWindows };
}

function fmtSignedSats(value) {
  const rounded = Math.round(value);
  if (rounded === 0) return "0";
  return `${rounded > 0 ? "+" : "−"}${fmtSats(Math.abs(rounded))}`;
}

function fmtSignedPct(value) {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) < 0.005) return "0.00%";
  return `${value > 0 ? "+" : "−"}${Math.abs(value).toFixed(2)}%`;
}

function StatCard({ label, value, subtext, highlight = false, negative = false }) {
  return (
    <div
      className={[
        "ss-card ss-card-pad",
        negative ? "border-rose-500/30 bg-rose-500/5" : highlight ? "border-orange-500/30 bg-orange-500/5" : "",
      ].join(" ")}
    >
      <p className="ss-section-title">{label}</p>
      <p className="text-3xl font-bold text-white tabular-nums leading-tight">
        {value}
      </p>
      {subtext ? (
        <p className="text-xs text-textMuted mt-1">{subtext}</p>
      ) : null}
    </div>
  );
}

function EntryContextPill({ entryPct }) {
  if (entryPct == null) return null;

  return (
    <div className="mt-2">
      <span className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 bg-zinc-800/50 px-2 py-1 text-[11px] text-zinc-300 tabular-nums">
        Entry · {fmtPct(entryPct)} below window start
      </span>
    </div>
  );
}

function PartnerTradeCard({ trade }) {
  const entryPct = getEntryPct(trade);
  const sats = getTradeSats(trade);

  return (
    <div className="border border-border rounded-2xl px-3 py-2 bg-zinc-950/40 overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-white font-semibold truncate">
            {fmtDate(trade.executed_at || trade.created_at)}
          </p>
          <p className="text-xs text-textMuted truncate">
            {trade.partner_name || "Partner"}
          </p>
        </div>

        <span
          className={[
            "shrink-0 text-xs px-2 py-1 rounded-full border",
            statusPill(trade.status),
          ].join(" ")}
        >
          {trade.status || "—"}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 text-xs tabular-nums">
        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">Sats</span>
          <span className="text-white font-semibold">{fmtSats(sats)}</span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">USD</span>
          <span className="text-white font-semibold">
            {fmtUsd(trade.usd_amount)}
          </span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">Px</span>
          <span className="text-white font-semibold">
            {fmtPrice(trade.execution_price)}
          </span>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-zinc-950/40 px-2 py-1.5">
          <span className="text-textMuted">Fee</span>
          <span className="text-white font-semibold">
            {fmtUsd(trade.partner_fee_usd)}
          </span>
        </div>
      </div>

      <EntryContextPill entryPct={entryPct} />

      {trade.failure_reason ? (
        <p className="text-xs text-red-300 mt-2">
          {trade.failure_reason}
        </p>
      ) : null}
    </div>
  );
}

export default function PartnerDashboard() {
  const navigate = useNavigate();

  const [stats, setStats] = React.useState(null);
  const [plans, setPlans] = React.useState([]);
  const [trades, setTrades] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [showAllTrades, setShowAllTrades] = React.useState(false);

  const primaryPartner =
    stats?.partners?.[0]?.partner_name ||
    plans?.[0]?.partner_name ||
    trades?.[0]?.partner_name ||
    "Partner";

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const [statsRes, plansRes, tradesRes] = await Promise.all([
          api.get("/me/partner-stats"),
          api.get("/me/partner-plans"),
          api.get("/me/partner-trades?limit=50"),
        ]);

        if (cancelled) return;

        const statsData = statsRes?.data || null;
        const plansData = Array.isArray(plansRes?.data?.plans)
          ? plansRes.data.plans
          : [];
        const tradesData = Array.isArray(tradesRes?.data?.trades)
          ? tradesRes.data.trades
          : [];

        setStats(statsData);
        setPlans(plansData);
        setTrades(tradesData);

        afTrack("partner_dashboard_view", {
          partner_count: statsData?.partners?.length || 0,
          plan_count: plansData.length,
          trade_count: tradesData.length,
        });
      } catch (e) {
        if (cancelled) return;

        const msg =
          e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          "Could not load partner history.";

        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const hasPartnerData =
    (stats?.partners?.length || 0) > 0 ||
    plans.length > 0 ||
    trades.length > 0;

  const totals = stats?.totals || {};
  const timingResult = getCompletedResult(totals, stats?.benchmark);

  const displayedTrades = showAllTrades ? trades : trades.slice(0, 5);
  const hasMoreTrades = trades.length > displayedTrades.length;

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="max-w-5xl mx-auto">
          <div className="ss-card ss-card-pad animate-pulse">
            <div className="h-5 w-48 rounded bg-zinc-700 mb-4" />
            <div className="h-4 w-80 rounded bg-zinc-800" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="max-w-xl mx-auto ss-card ss-card-pad text-center">
          <p className="text-lg font-semibold text-white mb-2">
            Could not load partner history
          </p>
          <p className="text-sm text-textMuted mb-5">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="ss-btn-primary px-4 py-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!hasPartnerData) {
    return (
      <div className="p-4 sm:p-6">
        <div className="max-w-xl mx-auto ss-card ss-card-pad text-center">
          <p className="text-lg font-semibold text-white mb-2">
            No linked partner history yet
          </p>
          <p className="text-sm text-textMuted mb-5">
            Once you link partner Smart Timing history, your read-only stats and
            transactions will appear here.
          </p>
          <button
            type="button"
            onClick={() => navigate("/partner-start")}
            className="ss-btn-primary px-4 py-2"
          >
            Request a new link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      <div className="max-w-5xl mx-auto flex flex-col gap-4">
        <div className="ss-card ss-card-pad border border-orange-500/20 bg-orange-500/5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-orange-300 mb-2">
                Partner Smart Timing
              </p>

              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                {primaryPartner} history
              </h1>

              <p className="text-sm text-zinc-300 mt-2 max-w-2xl">
                Read-only Smart Timing activity powered by SatStacker and
                managed through {primaryPartner}. To change your amount,
                frequency, funding source, or exchange settings, use{" "}
                {primaryPartner}.
              </p>
            </div>

            <div className="inline-flex w-fit rounded-full border border-zinc-800 bg-zinc-950/50 px-3 py-1.5 text-xs text-textMuted">
              Managed externally
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Sats stacked"
            value={fmtSats(totals.total_sats)}
            subtext={`${fmtBtc(totals.total_btc)} BTC`}
            highlight
          />

          <StatCard
            label="Smart Timing result"
            value={timingResult ? `${fmtSignedSats(timingResult.deltaSats)} sats` : "—"}
            subtext={
              timingResult
                ? `${fmtSignedPct(timingResult.gainPct)} vs scheduled DCA · ${timingResult.completedWindows} completed ${timingResult.completedWindows === 1 ? "window" : "windows"}`
                : stats?.benchmark === "scheduled_window_end"
                  ? "Your result appears after a completed buy window"
                  : "Waiting for updated Smart Timing results"
            }
            highlight={Boolean(timingResult && timingResult.deltaSats > 0)}
            negative={Boolean(timingResult && timingResult.deltaSats < 0)}
          />

          <StatCard
            label="USD executed"
            value={fmtUsd(totals.total_usd)}
          />

          <StatCard
            label="Total buys"
            value={Number(totals.trade_count || 0).toLocaleString()}
          />
        </div>

        {plans.length > 0 ? (
          <div className="ss-card ss-card-pad">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-semibold text-white">Plans</p>
                <p className="text-xs text-textMuted">
                  Read-only. Managed through the partner exchange.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {plans.map((plan) => (
                <div
                  key={`${plan.partner_slug}-${plan.partner_plan_id}`}
                  className="rounded-2xl border border-border bg-zinc-950/40 p-4"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {plan.partner_name || "Partner"} plan
                      </p>
                      <p className="text-xs text-textMuted">
                        {fmtUsd(plan.amount_usd)} · {plan.frequency}
                      </p>
                    </div>

                    <span
                      className={[
                        "text-xs px-2 py-1 rounded-full border",
                        statusPill(plan.status),
                      ].join(" ")}
                    >
                      {plan.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-xl border border-border bg-zinc-950/40 p-3">
                      <p className="text-textMuted">Buys</p>
                      <p className="text-white font-semibold tabular-nums">
                        {Number(plan.trade_count || 0).toLocaleString()}
                      </p>
                    </div>

                    <div className="rounded-xl border border-border bg-zinc-950/40 p-3">
                      <p className="text-textMuted">Stacked</p>
                      <p className="text-white font-semibold tabular-nums">
                        {fmtSats(plan.total_sats)} sats
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-textMuted mt-3">
                    Next check: {fmtDate(plan.next_run)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="ss-card ss-card-pad">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-sm font-semibold text-white">Transactions</p>
              <p className="text-xs text-textMuted">
                Partner-executed buys. Entry percentages show price vs window start, not final performance.
              </p>
            </div>

            {trades.length > 0 ? (
              <span className="text-xs text-textMuted">
                Latest {displayedTrades.length}
              </span>
            ) : null}
          </div>

          {trades.length === 0 ? (
            <div className="rounded-xl border border-border bg-zinc-950/40 p-4 text-sm text-textMuted">
              No transactions yet.
            </div>
          ) : (
            <>
              <div className="sm:hidden flex flex-col gap-2">
                {displayedTrades.map((trade) => (
                  <PartnerTradeCard key={trade.id} trade={trade} />
                ))}
              </div>

              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm tabular-nums">
                  <thead className="ss-thead">
                    <tr>
                      <th className="p-3 text-left font-medium">Date</th>
                      <th className="p-3 text-left font-medium">Partner</th>
                      <th className="p-3 text-left font-medium">Status</th>
                      <th className="p-3 text-right font-medium">Sats</th>
                      <th className="p-3 text-right font-medium">USD</th>
                      <th className="p-3 text-right font-medium">Price</th>
                      <th className="p-3 text-right font-medium">Fee</th>
                      <th className="p-3 text-right font-medium">Entry</th>
                    </tr>
                  </thead>

                  <tbody>
                    {displayedTrades.map((trade) => {
                      const entryPct = getEntryPct(trade);

                      return (
                        <tr
                          key={trade.id}
                          className="border-b border-border"
                        >
                          <td className="p-3 text-left whitespace-nowrap text-white">
                            {fmtDate(trade.executed_at || trade.created_at)}
                          </td>

                          <td className="p-3 text-left text-zinc-300">
                            {trade.partner_name || "Partner"}
                          </td>

                          <td className="p-3 text-left">
                            <span
                              className={[
                                "text-xs px-2 py-1 rounded-full border",
                                statusPill(trade.status),
                              ].join(" ")}
                            >
                              {trade.status || "—"}
                            </span>
                          </td>

                          <td className="p-3 text-right text-white">
                            {fmtSats(getTradeSats(trade))}
                          </td>

                          <td className="p-3 text-right text-white">
                            {fmtUsd(trade.usd_amount)}
                          </td>

                          <td className="p-3 text-right text-white">
                            {fmtPrice(trade.execution_price)}
                          </td>

                          <td className="p-3 text-right text-white">
                            {fmtUsd(trade.partner_fee_usd)}
                          </td>

                          <td className="p-3 text-right text-zinc-300 whitespace-nowrap">
                            {entryPct != null
                              ? `${fmtPct(entryPct)} below start`
                              : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {trades.length > 5 ? (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowAllTrades((v) => !v)}
                    className="ss-btn-primary px-4 py-2 w-full sm:w-auto"
                  >
                    {showAllTrades ? "Show less" : "Show more"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="ss-card ss-card-pad bg-zinc-950/40">
          <p className="text-sm font-semibold text-white mb-1">
            Managed through {primaryPartner}
          </p>
          <p className="text-sm text-textMuted">
            This page is read-only. To change your amount, frequency, funding
            source, or exchange settings, open {primaryPartner}.
          </p>
        </div>
      </div>
    </div>
  );
}